function safe(value) {
  return value === null || value === undefined ? '' : String(value);
}

function renderSection(title, bodyContent) {
  return `
    <gov-accordion-item size="xs" variant="primary">
      <h4 slot="label">${title}</h4>
      ${bodyContent}
    </gov-accordion-item>`;
}

function sortOperationsByDate(operations) {
  return [...operations].sort((a, b) => {
    const parseDateStr = (dateStr) => {
      if (!dateStr) return 0;
      const parts = String(dateStr).split(' ');
      const [day, month, year] = (parts[0] || '').split('.');
      const [hour = '0', minute = '0', second = '0'] = (parts[1] || '').split(':');
      return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    };
    return parseDateStr(a?.datum).getTime() - parseDateStr(b?.datum).getTime();
  });
}

function buildCaseDetailMarkup(detail) {
  if (!detail) {
    return '<div class="case-detail case-detail--empty">Detail se nepodařilo načíst.</div>';
  }

  const poplatekRaw = safe(detail.spravniPoplatekUhrazen).toUpperCase();
  let poplatek = '';
  if (poplatekRaw === 'U') poplatek = 'Ano (uhrazeno)';
  else if (poplatekRaw === 'N') poplatek = 'Ne (neuhrazeno)';
  else if (poplatekRaw) poplatek = safe(detail.spravniPoplatekUhrazen);

  const operace = Array.isArray(detail?.operace) ? detail.operace : [];
  const ucastnici = Array.isArray(detail?.ucastnici) ? detail.ucastnici : [];
  const souvisejiciDokumenty = Array.isArray(detail?.souvisejiciDokumenty) ? detail.souvisejiciDokumenty : [];
  const listiny = Array.isArray(detail?.listiny) ? detail.listiny : [];
  const nemovitosti = Array.isArray(detail?.nemovitosti)
    ? detail.nemovitosti.filter((item) => safe(item?.nazev).trim().length)
    : [];

  const operaceHtml = operace.length
    ? renderSection(
        'Průběh řízení',
        `<div class="table-wrapper">
          <table class="cases-table case-operations-table">
            <thead>
              <tr><th>Datum</th><th>Úkon</th></tr>
            </thead>
            <tbody>
              ${sortOperationsByDate(operace)
                .map(
                  (op) =>
                    `<tr><td>${safe(op.datum)}</td><td>${op.staNahlizeni ? safe(op.staNahlizeni) : ''}</td></tr>`
                )
                .join('')}
            </tbody>
          </table>
        </div>`
      )
    : '';

  const ucastniciHtml = ucastnici.length
    ? renderSection(
        'Účastníci',
        `<div class="table-wrapper">
          <table class="cases-table case-participants-table">
            <thead>
              <tr><th>Jméno</th><th>Datum narození / IČO</th><th>Role</th></tr>
            </thead>
            <tbody>
              ${ucastnici
                .map(
                  (u) =>
                    `<tr><td>${safe(u.jmeno)}</td><td>${u.icoRc ? safe(u.icoRc) : ''}</td><td>${u.typUca ? safe(u.typUca) : ''}</td></tr>`
                )
                .join('')}
            </tbody>
          </table>
        </div>`
      )
    : '';

  const dokumentyHtml = souvisejiciDokumenty.length
    ? renderSection(
        'Související dokumenty',
        `<div class="table-wrapper">
          <table class="cases-table case-docs-table">
            <tbody>
              ${souvisejiciDokumenty.map((d) => `<tr><td>${safe(d.nazev)}</td></tr>`).join('')}
            </tbody>
          </table>
        </div>`
      )
    : '';

  const listinyHtml = listiny.length
    ? renderSection(
        'Listiny',
        `<div class="table-wrapper">
          <table class="cases-table case-docs-table">
            <tbody>
              ${listiny.map((l) => `<tr><td>${safe(l.nazev)}</td></tr>`).join('')}
            </tbody>
          </table>
        </div>`
      )
    : '';

  const nemovitostiHtml = nemovitosti.length
    ? renderSection(
        'Nemovitosti',
        `<div class="table-wrapper">
          <table class="cases-table case-docs-table">
            <tbody>
              ${nemovitosti.map((n) => `<tr><td>${safe(n.nazev)}</td></tr>`).join('')}
            </tbody>
          </table>
        </div>`
      )
    : '';

  const showMobileMeta = Boolean(detail?.datum || detail?.stav);
  const mobileMetaHtml = showMobileMeta
    ? `
        ${
          detail.datum
            ? `<dt class="case-detail__meta--mobile">Datum</dt><dd class="case-detail__meta--mobile">${safe(
                detail.datum
              )}</dd>`
            : ""
        }
        ${
          detail.stav
            ? `<dt class="case-detail__meta--mobile">Stav</dt><dd class="case-detail__meta--mobile">${safe(
                detail.stav
              )}</dd>`
            : ""
        }
      `
    : "";

  return `
    <div class="case-detail">
      <dl>
        ${mobileMetaHtml}
        ${
          detail.pracNazev
            ? `<dt>Pracoviště</dt><dd>${safe(detail.pracNazev)}</dd>`
            : ""
        }
        ${
          poplatek
            ? `<dt>Správní poplatek uhrazen</dt><dd>${poplatek}</dd>`
            : ""
        }
      </dl>
      ${detail.popis ? `<p>${safe(detail.popis)}</p>` : ""}
      ${ucastniciHtml}
      ${nemovitostiHtml}
      ${listinyHtml}
      ${dokumentyHtml}
      ${operaceHtml}
    </div>
  `;
}

async function fetchCaseDetailData(id) {
  const response = await fetch(`/DetailRizeni?rizeniId=${encodeURIComponent(id)}`);
  if (!response.ok) {
    throw new Error('Failed to fetch case detail');
  }
  return response.json();
}

async function getCaseDetailMarkup(id) {
  try {
    const detail = await fetchCaseDetailData(id);
    const markup = buildCaseDetailMarkup(detail);
    const title = `Řízení č. ${safe(detail?.cisloRizeni ?? detail?.cislo_rizeni ?? id)}`;
    return { markup, detail, title };
  } catch (error) {
    return {
      markup: '<div class="case-detail case-detail--empty">Detail se nepodařilo načíst.</div>',
      detail: null,
      title: 'Detail řízení',
      error
    };
  }
}

export { getCaseDetailMarkup, buildCaseDetailMarkup, fetchCaseDetailData };
